import { PRODUCTS, createDesign, normalizeDesign, createLayer } from './design-model.js';

function collectionDesign(name, palette, { shape = 'ns-028', length = 'medium', pearl = false, french = false } = {}) {
  const base = createDesign();
  return normalizeDesign({ ...base, name, shape, shapeVariant: shape === 'ns-044' ? 8 : 4, length,
    nails: base.nails.map((nail, index) => ({ ...nail, color: palette[index % palette.length], customColor: null, finish: 'glossy',
      french: { ...nail.french, enabled: french && index % 5 === 1, color: '#fff9ee', depth: 20 },
      layers: pearl && index % 5 === 3 ? [createLayer('charm', { type: 'ref-pearl-white', x: 50, y: 44, sizeMm: 2.4, scale: .4 })] : [],
    })),
  });
}

const collections = {
  'rose-set': { slug: 'rose-letter', english: 'A LETTER IN SPRING', tagline: '软浪漫', taglineEn: 'Soft Romance', category: 'nails', mood: '温柔日常', tone: 'rose', style: 'soft-romance', styleZh: '软浪漫', styleTags: ['soft-romance', 'Soft Romance', '软浪漫'], shapeTags: ['almond'], lengthTags: ['medium'], craftTier: 'simple-art',
    description: '把一封没有寄出的情书，藏进柔和的玫瑰色里。粉调底胶与一颗小珍珠，留下刚刚好的温柔。', emotionalDescription: '有些柔软无需解释。让玫瑰粉色和一丝闪光，在指尖带着一封未寄出的情书。', emotionalDescriptionEn: 'Some softness needs no explanation. Let rose pink and a little shimmer carry an unmailed love letter on your fingertips.',
    details: ['十指成套配色，左右手呼应', '自然甲型与玫瑰粉调', '无名指珍珠点缀'],
    design: collectionDesign('春日来信', ['ref-BG-C-094', 'ref-BG-A-026', 'ref-BG-C-031', 'ref-BG-A-080', 'ref-BG-A-027'], { pearl: true }) },
  'moon-set': { slug: 'moonlight-sonata', english: 'MOONLIGHT SONATA', category: 'nails', mood: '清透微光', tone: 'moon', style: 'chrome-aura', styleZh: '未来感镜面', styleTags: ['chrome-aura', 'Chrome & Aura', '未来感镜面'], shapeTags: ['almond'], lengthTags: ['medium'], craftTier: 'detailed-art',
    description: '像夜晚落在窗边的一点月光。奶白、银调与细小的光泽，让日常也有安静的仪式感。',
    details: ['奶白与银调底胶交替搭配', '自然中长甲型', '局部法式线条与微光细节'],
    design: collectionDesign('月光奏鸣曲', ['ref-BG-A-080', 'ref-BG-A-079', 'ref-BG-C-043', 'ref-BG-A-060', 'ref-BG-A-080'], { french: true, pearl: true }) },
  'cherry-set': { slug: 'cherry-after-hours', english: 'CHERRY AFTER HOURS', category: 'nails', mood: '复古浓色', tone: 'cherry', style: 'quiet-luxury', styleZh: '低调奢华', styleTags: ['quiet-luxury', 'Quiet Luxury', '低调奢华'], shapeTags: ['coffin'], lengthTags: ['short'], craftTier: 'simple-art',
    description: '樱桃、红酒，还有一点随性的光泽。用深浅红调搭出层次，让每一次举手都有自己的表情。',
    details: ['深浅红调组成十指配色', '短款甲型，利落的日常比例', '保留底胶自身光泽与层次'],
    design: collectionDesign('微醺樱桃', ['ref-BG-C-010', 'ref-BG-C-015', 'ref-BG-A-049', 'ref-BG-C-016', 'ref-BG-C-010'], { shape: 'ns-044', length: 'short' }) },
  'care-kit': { slug: 'nail-care-essentials', english: 'THE LITTLE ESSENTIALS', category: 'care', mood: '日常护理', tone: 'care',
    description: '为佩戴前后的指尖护理预留一个小位置。套装实拍、具体清单与使用说明正在整理，确认后会在这里补充。',
    details: ['套装清单待确认', '实物图片待补充', '具体使用方法以最终商品说明为准'], design: null },
};

export const STORE_PRODUCTS = PRODUCTS.map(product => ({ ...product, ...collections[product.id] }));
export const CUSTOM_STEPS = [
  { number: '01', title: '挑一个喜欢的起点', text: '从空白甲面开始，或挑选一套作品作为灵感。' },
  { number: '02', title: '把想法搭配出来', text: '选择甲型与底色，叠加饰品、手绘和属于你的细节。' },
  { number: '03', title: '确认十指与尺寸', text: '核对左右手搭配、甲片尺寸和完整设计预览。' },
  { number: '04', title: '设计完成，再去结账', text: '确认订单金额与收货信息；付款后进入定制制作流程。' },
];
export const STORE_FAQS = [
  { question: '是在设计完成之后付款吗？', answer: '是的。你可以先在设计室完成十指搭配，确认尺寸与设计，再进入结账。我们会按确认后的订单设计安排后续制作。当前站点为预览阶段，实际支付开放情况以结账页提示为准。' },
  { question: '能从一套现成款继续修改吗？', answer: '可以。进入作品详情，选择“带着这套灵感去定制”，就能继续修改甲型、底色、饰品和每一指的细节。修改后的价格会按最终设计重新计算。' },
  { question: '不知道自己的甲片尺寸怎么办？', answer: '先阅读尺寸指南，分别记录左右手十指。设计室支持标准尺寸、逐指 0–9 号甲片和毫米尺寸；0 号是有效甲片号。不同甲型的号数可能存在差异，不能把号数直接当成毫米。' },
  { question: '设计可以保存，下次再继续吗？', answer: '可以在设计室保存作品、导出完整设计文件或生成设计码。换设备时可通过设计码或完整设计文件重新载入；仅保存预览图片无法恢复所有可编辑细节。' },
  { question: '屏幕里的效果和实物完全一样吗？', answer: '屏幕、光线以及手工制作会带来颜色与细节差异。页面展示的是设计预览；实际材质、可制作工艺与最终交付说明以确认订单时的信息为准。' },
  { question: '多久制作、如何配送或售后？', answer: '具体制作周期、配送地区与费用、售后处理条件正在确认。正式接单前会在服务说明和订单确认页写清楚；目前没有承诺固定发货时间。' },
];
export const STORE_POLICIES = {
  privacy: { title: '隐私与数据说明', intro: '服务说明草稿 · 正式经营信息确认后更新', sections: [
    ['你在这里留下的信息', '设计、尺寸、账户和订单信息用于保存作品、核对定制需求与处理订单。请勿在公开设计名称或分享内容中填写地址、联系方式等私人信息。'],
    ['本地保存与设计分享', '设计室可在当前浏览器保存作品。设计码用于重新打开设计；请只向你愿意分享设计的人提供。完整设计文件可自行备份。'],
    ['需要进一步确认的事项', '经营主体、数据保存期限、删除与访问申请渠道仍待确认。正式接单前会补充完整的信息处理说明。'],
  ] },
  shipping: { title: '制作与配送说明', intro: '服务说明草稿 · 配送配置待确认', sections: [
    ['先确认设计，再安排制作', '请在下单前核对十指搭配、甲型、长度与尺寸。制作将依据订单保存的设计版本，而不是你之后继续编辑的版本。'],
    ['制作周期与运费', '具体制作周期、可配送地区、运费与物流方式待确认。请以正式开放结账后的订单页面信息为准，当前不承诺固定发货时间。'],
    ['收货信息', '请在提交订单前核对收件人和地址。如需修改已提交订单的信息，请通过站内联系页面说明订单情况。'],
  ] },
  returns: { title: '售后与修改说明', intro: '服务说明草稿 · 售后条件待确认', sections: [
    ['设计修改', '加入购物袋前可以继续修改设计。订单提交后的修改需要结合实际制作进度确认，不能通过修改本地作品自动更新已提交订单。'],
    ['尺寸与效果核对', '请认真测量并分别记录十指尺寸。实物与屏幕预览可能因光线、显示设备和制作工艺存在差异。'],
    ['售后服务', '退换、修补、取消和退款的适用条件与流程正在确认。正式服务条款公布前，本页不构成具体退换承诺，也不限制法律赋予消费者的权利。'],
  ] },
  terms: { title: '定制服务说明', intro: '服务说明草稿 · 经营资料待确认', sections: [
    ['你的设计与订单', '请确保使用的上传图片和图案已获适当授权。可编辑设计仅用于表达需求，最终可制作性需要结合材料与工艺确认。'],
    ['价格与支付', '页面展示预览价格。定制金额应由订单确认页按照设计内容重新核算；正式开放支付前不会将预览操作视为已付款订单。'],
    ['正式经营信息', '经营主体、联系方式与正式服务条款仍待确认，完成后将在本站公布。'],
  ] },
};
